import React, { FC, Fragment, useContext } from 'react';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { subcontractOrderTemplate } from 'apollo/templates/order/mutation.graphql';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useSubcontractOrder } from 'pages/order-flow/subcontract-order/subcontract-order-context';
import {
    subcontractCommentOf,
    subcontractOrderInputOf,
} from 'components/order-flow/subcontract-order/subcontract-input-helper';
import InfoText from 'components/shared/info-text.component';
import { formatDate } from 'utils/format';

type ConfirmSubcontractStepProps = {
    onBackClick: () => void;
};

/** The delivery date in the user's date format, with the time when one was chosen. */
const formatDeliveryDate = (date: Date | null, time: { hour?: number; minute?: number }): string => {
    if (!date) return '';
    if (time.hour === undefined) return formatDate(date, 'date');
    const withTime = new Date(date);
    withTime.setHours(time.hour, time.minute ?? 0, 0, 0);
    return formatDate(withTime, 'datetime');
};

/**
 * DD-3484: only on confirmation does the sub-order exist. The mutation sends ids and
 * toggles only — the server reads the parent order and builds the sub-order from the
 * selection, so nothing unselected can travel whatever the client sends.
 */
const ConfirmSubcontractStep: FC<ConfirmSubcontractStepProps> = ({ onBackClick }) => {
    const { t: translate } = useTranslation(['orders', 'client-directory', 'common']);
    const { parentOrder, subcontract } = useSubcontractOrder();
    const { selection } = subcontract;
    const navigate = useNavigate();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const [subcontractOrder, { loading }] = useMutation(subcontractOrderTemplate, {
        onCompleted: ({ subcontractOrder }) => {
            navigate(`/order-details/${subcontractOrder._id}/main-view`);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_CREATED') });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const selectedFileCount = selection.fileIds.length + selection.scannerCaseFileIds.length;
    const dentistLabel = translate('orders:DENTIST');
    const comment = subcontractCommentOf(parentOrder, subcontract, dentistLabel);
    const input = subcontractOrderInputOf(parentOrder, subcontract, dentistLabel);
    const yesOrNo = (value: boolean) => translate(value ? 'common:YES' : 'common:NO');

    const onConfirmClick = () => {
        subcontractOrder({ variables: { input } });
    };

    const summaryRows: { label: string; value: string }[] = [
        { label: translate('client-directory:SUPPLIER'), value: subcontract.supplier?.name ?? '' },
        { label: translate('orders:RESPONSIBLE_USER'), value: subcontract.responsibleUser?.fullName ?? '' },
        {
            label: translate('orders:CONSTRUCTIONS'),
            value: String(selection.restorationIds.length),
        },
        { label: translate('common:FILES'), value: String(selectedFileCount) },
        { label: translate('orders:PATIENT'), value: yesOrNo(input.selection.includePatientReference) },
        {
            label: translate('orders:SUBCONTRACT_PATIENT_IDENTITY_NUMBER'),
            value: yesOrNo(input.selection.includePatientIdentityNumber),
        },
        { label: translate('orders:DENTIST'), value: yesOrNo(input.selection.includeDentistName) },
        {
            label: translate('orders:SAMPLE_TAKEN'),
            value: input.sampleType ? translate(`orders:${input.sampleType}`) : '',
        },
        {
            label: translate('orders:DELIVERY_DATE'),
            value: formatDeliveryDate(subcontract.deliveryDate, subcontract.deliveryTime),
        },
    ];

    return (
        <Fragment>
            <Box mb={2}>
                <Typography variant="h1">{translate('orders:CONFIRM')}</Typography>
                <InfoText text={translate('orders:SUBCONTRACT_CONFIRM_INFO')} />
            </Box>
            <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box p={2}>
                    <Grid container spacing={1}>
                        {summaryRows.map((row) => (
                            <Fragment key={row.label}>
                                <Grid item xs={6} md={4}>
                                    <Typography variant="subtitle2">{row.label}</Typography>
                                </Grid>
                                <Grid item xs={6} md={8}>
                                    <Typography variant="body2">{row.value}</Typography>
                                </Grid>
                            </Fragment>
                        ))}
                    </Grid>
                </Box>
            </Paper>
            <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box p={2}>
                    <Typography variant="subtitle2" mb={1}>
                        {translate('orders:SUBCONTRACT_MESSAGE_TO_SUBCONTRACTOR')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color={comment ? 'text.primary' : 'text.secondary'}
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        data-test="confirm-subcontract-step.comment"
                    >
                        {comment || translate('orders:NO_COMMENTS_AVAILABLE')}
                    </Typography>
                </Box>
            </Paper>
            <Box display="flex" justifyContent="space-between">
                <Button onClick={onBackClick} data-test="confirm-subcontract-step.back-btn">
                    {translate('common:BACK')}
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={loading}
                    onClick={onConfirmClick}
                    data-test="confirm-subcontract-step.confirm-btn"
                >
                    {translate('orders:SUBCONTRACT_ORDER')}
                </LoadingButton>
            </Box>
        </Fragment>
    );
};

export default ConfirmSubcontractStep;
